// Translations in the browser.
//
// The messages are the Rails ones (config/locales/*.yml), sent by I18nTree as window.I18N with the
// user's language laid over English. Keeping Rails' format (%{name} placeholders, one/other plural
// keys) means one set of files serves ERB views, Rails messages and Vue.
//
//   t('app.jobs.title')                          -> "Job Listings"
//   t('app.jobs.count', { count: 3 })            -> "3 jobs"        (plural keys: one / other / ...)
//   t('app.hello', { name: 'Ada' })              -> "Hello Ada"     ("Hello %{name}")
//
// Language changes with a page load (?locale=de), so nothing here needs to be reactive.
// Missing keys are bugs: development warns once and the key is shown so it cannot be missed.

let tree = null
let source = null
const warned = new Set()

// Parsed once per page. A Turbo visit that changes the language writes a new window.I18N, which
// is why the cache remembers the string it was built from.
export function translations() {
  if (tree && source === window.I18N) return tree
  source = window.I18N
  try {
    tree = JSON.parse(source || '{}')
  } catch {
    tree = {}
  }
  return tree
}

// For tests, and for anything that replaces window.I18N after load.
export function resetTranslations() {
  tree = null
  source = null
  warned.clear()
}

export function locale() {
  return window.CURRENT_LOCALE || 'en'
}

// Languages the help center has its own copy of under /help-center/<locale>/ (the same list as
// MarketingHelper::HELP_CENTER_LOCALES). Everything else links to the English help center.
const HELP_CENTER_LOCALES = ['de']

// A help center link in the language of the page. Pass the guide as `path` ('job-tracker/fetch-jobs',
// an anchor is fine, '' for the index).
export function helpCenterUrl(path = '') {
  const lang = locale()
  const base = HELP_CENTER_LOCALES.includes(lang) ? `/help-center/${lang}` : '/help-center'
  return path ? `${base}/${path}` : `${base}/`
}

function lookup(key) {
  let node = translations()
  for (const part of String(key).split('.')) {
    if (node === null || typeof node !== 'object') return undefined
    node = node[part]
  }
  return node
}

function pluralCategory(count) {
  try {
    return new Intl.PluralRules(locale()).select(count)
  } catch {
    return count === 1 ? 'one' : 'other'
  }
}

function interpolate(message, vars) {
  return message.replace(/%\{(\w+)\}/g, (placeholder, name) => (name in vars ? String(vars[name]) : placeholder))
}

function warnMissing(key) {
  if (warned.has(key)) return
  warned.add(key)
  // eslint-disable-next-line no-console
  if (import.meta.env?.DEV) console.warn(`[i18n] missing translation: ${key}`)
}

// True when the key resolves to a message (or a set of plural forms).
export function te(key) {
  const found = lookup(key)
  return typeof found === 'string' || (found !== null && typeof found === 'object' && ('other' in found || 'one' in found))
}

export function t(key, vars = {}) {
  let message = lookup(key)

  if (message !== null && typeof message === 'object') {
    const category = 'count' in vars ? pluralCategory(Number(vars.count)) : 'other'
    message = vars.count === 0 && 'zero' in message ? message.zero : (message[category] ?? message.other)
  }

  if (typeof message !== 'string') {
    warnMissing(key)
    return key
  }
  return interpolate(message, vars)
}
